/**
 * gameSlice — manages core game state.
 */
import { createSlice } from '@reduxjs/toolkit';
import { GAME_STATUS, GAME_MODE, WORD_LENGTH } from '../../utils/constants';

const initialState = {
  // The target word (uppercase Turkish)
  targetWord: '',
  // Array of guessed words (strings)
  guesses: [],
  // Array of evaluations for each guess (array of TILE_STATE arrays)
  evaluations: [],
  // Current input row (letters being typed)
  currentGuess: '',
  // Game status: idle | playing | won | lost
  status: GAME_STATUS.IDLE,
  // Game mode: classic | custom
  mode: GAME_MODE.CLASSIC,
  // Current row index (0-based)
  currentRow: 0,
  // Error/toast message to display
  message: '',
  // Whether row should shake (invalid input)
  shakeRow: false,
  // Custom game code (for custom mode)
  gameCode: '',
  // Flag: dictionary loaded
  dictionaryLoaded: false,
};

const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    /**
     * Initialize game with target word and mode.
     */
    initGame(state, action) {
      const { targetWord, mode, gameCode } = action.payload;
      state.targetWord = targetWord;
      state.mode = mode;
      state.gameCode = gameCode || '';
      state.guesses = [];
      state.evaluations = [];
      state.currentGuess = '';
      state.status = GAME_STATUS.PLAYING;
      state.currentRow = 0;
      state.message = '';
      state.shakeRow = false;
    },

    /**
     * Restore game state (from localStorage).
     */
    restoreGame(state, action) {
      return { ...state, ...action.payload, dictionaryLoaded: state.dictionaryLoaded };
    },

    /**
     * Add a letter to the current guess.
     */
    addLetter(state, action) {
      if (
        state.status !== GAME_STATUS.PLAYING ||
        state.currentGuess.length >= WORD_LENGTH
      ) {
        return;
      }
      state.currentGuess += action.payload;
    },

    /**
     * Remove the last letter from the current guess.
     */
    removeLetter(state) {
      if (state.status !== GAME_STATUS.PLAYING || state.currentGuess.length === 0) {
        return;
      }
      state.currentGuess = state.currentGuess.slice(0, -1);
    },

    /**
     * Submit the current guess. Saga will handle validation.
     * This is the action that triggers the submitGuessSaga.
     */
    submitGuess(state) {
      // Handled by saga — no direct state change here
    },

    /**
     * After saga validates, record the guess and its evaluation.
     */
    recordGuess(state, action) {
      const { guess, evaluation } = action.payload;
      state.guesses.push(guess);
      state.evaluations.push(evaluation);
      state.currentGuess = '';
      state.currentRow += 1;
    },

    /**
     * Set the game result (won / lost).
     */
    setGameResult(state, action) {
      state.status = action.payload;
    },

    /**
     * Show a temporary message (toast).
     */
    setMessage(state, action) {
      state.message = action.payload;
    },

    /**
     * Clear message.
     */
    clearMessage(state) {
      state.message = '';
    },

    /**
     * Trigger row shake.
     */
    setShakeRow(state, action) {
      state.shakeRow = action.payload;
    },

    /**
     * Mark dictionary as loaded.
     */
    setDictionaryLoaded(state, action) {
      state.dictionaryLoaded = action.payload;
    },

    /**
     * Reset game to initial state.
     */
    resetGame() {
      return { ...initialState };
    },

    /**
     * Request to start a classic game (triggers saga).
     */
    startClassicGame(state, action) {
      // payload: { isRandom: boolean } — saga handles
    },

    /**
     * Request to decode and start a custom game (triggers saga).
     */
    startCustomGame(state, action) {
      // payload: { gameCode }  — saga handles
    },

    /**
     * Request to create a custom game (triggers saga).
     */
    createCustomGame(state, action) {
      // payload: { word } — saga handles
    },

    /**
     * Set the generated game code after creating a custom game.
     */
    setGameCode(state, action) {
      state.gameCode = action.payload;
    },
  },
});

export const {
  initGame,
  restoreGame,
  addLetter,
  removeLetter,
  submitGuess,
  recordGuess,
  setGameResult,
  setMessage,
  clearMessage,
  setShakeRow,
  setDictionaryLoaded,
  resetGame,
  startClassicGame,
  startCustomGame,
  createCustomGame,
  setGameCode,
} = gameSlice.actions;

export default gameSlice.reducer;
